import { useContext, useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Button } from '../../../components';
import CustomInput from '../../../components/CustomInput';
import { DisplayFieldType } from '../../../components/DisplayForms';
import InfiniteScrollDropdown from '../../../components/Dropdown/InfiniteScrollDropdown';
import { logError } from '../../../components/LogError';
import { AppContext } from '../../../context/ContextProvider';
import { appIsWorkingVar } from '../../../graphql/cache';
import { useImp } from '../../../hooks/useImp';
import { useImpersonation } from '../../../hooks/useImpersonation';
import { useImpersonationSession } from '../../../hooks/useImpersonationSession';
import { getUserToken, setImpersonationInfo, updateUserToken } from '../../../services/auth';
import { NavigationPath } from '../../../types/DomainTypes';

const SUCCEED = 'succeed';
const ACTIVE = 'active';

const Impersonation = () => {
  const { t } = useTranslation();
  const {
    state: {
      settings: { email: rmEmail },
    },
  } = useContext(AppContext);

  const { verifyImpersonation, impersonateLogin } = useImpersonation();
  const { getClients, clients } = useImp(rmEmail);
  const { activeSession, endSession } = useImpersonationSession();

  const [canImpersonate, setCanImpersonate] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>('');
  const [selectedLabel, setSelectedLabel] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  const authSchema = yup.object({
    passcode: yup.string().required(t('account:impersonation.passcodeRequired')),
  });

  const formik = useFormik<yup.InferType<typeof authSchema>>({
    initialValues: { passcode: '' },
    validationSchema: authSchema,
    onSubmit: async (formData) => {
      try {
        const resp = await verifyImpersonation({ password: formData.passcode });
        if (resp.status?.toLowerCase() === SUCCEED) {
          setCanImpersonate(true);
          setErrorMessage(null);
          formik.resetForm();
          getClients({});
        } else {
          setErrorMessage(t('account:impersonation.authFailed'));
        }
      } catch (error) {
        logError(error as Error);
        setErrorMessage(t('account:impersonation.authFailed'));
      }
    },
  });

  const dropdownItems = useMemo(
    () =>
      clients.data.map((c) => ({
        id: c.userId || c.email,
        value: c.email,
        text: `${c.firstName} ${c.surname} (${c.email})`,
        content: (
          <span>
            {c.firstName} {c.surname} <span className="text-gray-500">({c.email})</span>
          </span>
        ),
      })),
    [clients.data],
  );

  const onImpersonate = async () => {
    if (!selectedEmail) {
      setErrorMessage(t('account:impersonation.selectClient'));
      return;
    }
    setIsImpersonating(true);
    appIsWorkingVar(true);
    try {
      const resp = await impersonateLogin({ impersonatedEmailAddress: selectedEmail });
      if (resp.status?.toLowerCase() !== SUCCEED || resp.result.sessionStatus?.toLowerCase() !== ACTIVE) {
        setErrorMessage(t('account:impersonation.linkFailed'));
        return;
      }
      const { userToken, impersonatorId, targetUserId, sessionExpiresAt } = resp.result;
      setImpersonationInfo({
        impersonatorId,
        targetUserId,
        targetUserName: selectedLabel,
        originalUserToken: getUserToken() || '',
        sessionExpiresAt,
      });
      updateUserToken(userToken);
      // Hard-reload onto the portfolio so the app re-initialises with the
      // impersonated client's token (wiping the RM's in-memory Apollo cache
      // and Context state) instead of needing a manual refresh.
      window.location.replace(NavigationPath.PORTFOLIO);
    } catch (error) {
      logError(error as Error);
      setErrorMessage(t('account:impersonation.linkFailed'));
    } finally {
      appIsWorkingVar(false);
      setIsImpersonating(false);
    }
  };

  useEffect(() => {
    if (clients.data.length === 0) return;
    const match = dropdownItems.find((item) => item.value === selectedEmail);
    if (match) setSelectedLabel(match.text);
  }, [dropdownItems, clients.data.length, selectedEmail]);

  if (activeSession) {
    return (
      <div className="flex flex-col gap-6 w-full p-6">
        <h2 className="text-xl font-bold">{t('account:impersonation.title')}</h2>
        <p className="text-sm text-green-600 dark:text-green-400">
          {t('account:impersonation.impersonatingNow', { name: activeSession.targetUserName })}
        </p>
        <Button type="button" className="btn-primary w-fit" onClick={endSession}>
          {t('account:impersonation.endSession')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 w-full p-6">
      <h2 className="text-xl font-bold">{t('account:impersonation.title')}</h2>

      {errorMessage && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
          {errorMessage}
        </div>
      )}

      {!canImpersonate ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            formik.handleSubmit();
          }}
          className="flex flex-col gap-4 max-w-md"
        >
          <label htmlFor="passcode" className="text-sm font-medium">
            {t('account:impersonation.enterPasscode')}
          </label>
          <CustomInput
            id="passcode"
            name="passcode"
            type={DisplayFieldType.PASSWORD}
            value={formik.values.passcode}
            onChange={(e) => formik.setFieldValue('passcode', (e.target as HTMLInputElement).value)}
            placeholder={t('account:impersonation.passcodePlaceholder')}
            helperText={formik.touched.passcode ? (formik.errors.passcode as string) : undefined}
          />
          <Button type="submit" className="btn-primary w-fit">
            {t('account:impersonation.authenticate')}
          </Button>
        </form>
      ) : (
        <div className="flex flex-col gap-4 w-full">
          <label htmlFor="accountHolder" className="text-sm font-medium">
            {t('account:impersonation.accountHolder')}
          </label>
          <InfiniteScrollDropdown
            id="accountHolder"
            className="w-full"
            containerClassName="w-full"
            items={dropdownItems}
            value={selectedEmail}
            valueText={selectedLabel}
            placeholder={t('account:impersonation.selectAccountHolder')}
            usePortal
            header={
              <div className="p-2 sticky top-0 bg-white dark:bg-gray-800 z-10">
                <input
                  type="text"
                  className="w-full border rounded-md px-3 py-2 text-sm dark:bg-gray-700"
                  placeholder={t('account:impersonation.search')}
                  onChange={(e) => clients.onSearch(e.target.value)}
                />
              </div>
            }
            onItemSelect={(item) => {
              setSelectedEmail(item.value);
              setSelectedLabel(item.text || item.value);
            }}
          />

          {selectedLabel && (
            <p className="text-sm text-green-600 dark:text-green-400">
              {t('account:impersonation.selected', { name: selectedLabel })}
            </p>
          )}

          <Button
            type="button"
            className="btn-primary w-fit"
            onClick={onImpersonate}
            isDisable={isImpersonating || !selectedEmail}
            isProcessing={isImpersonating}
          >
            {t('account:impersonation.impersonate')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Impersonation;
